import React from 'react';
import { format, startOfWeek, eachDayOfInterval, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Appointment, CalendarWorkingSettings } from './types';
import { CALENDAR_SETTINGS } from '../../config/calendarSettings';
import {
  APPOINTMENT_CARD_BASE_CLASS,
  APPOINTMENT_CARD_PADDING_COMPACT_CLASS,
  APPOINTMENT_CARD_TITLE_CLASS,
  APPOINTMENT_CARD_TIME_CLASS,
  getAppointmentStatusCardClass,
} from './statusStyles';
import {
  BASE_HOUR_SLOT_HEIGHT_PX,
  DEFAULT_WORKDAY_START_TIME,
  DEFAULT_WORKDAY_END_TIME,
  DEFAULT_BREAK_START_TIME,
  DEFAULT_BREAK_END_TIME,
} from '../../config/workingTimeDefaults';

interface WeekViewProps {
  currentDate: Date;
  appointments: Appointment[];
  workingSettings?: CalendarWorkingSettings;
  denseMode?: boolean;
  onAppointmentClick: (appointment: Appointment) => void;
  onTimeSlotClick: (startTime: Date, duration: number) => void;
  isLoading?: boolean;
}

const WEEK_CELL_VERTICAL_PADDING_PX = 16; // p-2 => 8px top + 8px bottom
const WEEK_APPOINTMENT_CARD_DENSE_HEIGHT_PX = 22;
const WEEK_APPOINTMENT_CARD_REGULAR_HEIGHT_PX = 40;
const WEEK_APPOINTMENT_GAP_DENSE_PX = 2;
const WEEK_APPOINTMENT_GAP_REGULAR_PX = 4;

const parseTimeToMinutes = (value: string): number | null => {
  const parts = value.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const hasMinutesOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number
): boolean => {
  return startA < endB && endA > startB;
};

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  appointments,
  workingSettings,
  denseMode = true,
  onAppointmentClick,
  onTimeSlotClick,
  isLoading = false
}) => {
  // Configuration
  const bookingStepMinutes = CALENDAR_SETTINGS.bookingStepMinutes;
  const rowHeight = Math.max(
    28,
    Math.round((bookingStepMinutes / 60) * BASE_HOUR_SLOT_HEIGHT_PX)
  );

  const workdayStartMinutes =
    parseTimeToMinutes(workingSettings?.workdayStartTime || DEFAULT_WORKDAY_START_TIME) ??
    CALENDAR_SETTINGS.workStartHour * 60;
  const workdayEndMinutes =
    parseTimeToMinutes(workingSettings?.workdayEndTime || DEFAULT_WORKDAY_END_TIME) ??
    CALENDAR_SETTINGS.workEndHour * 60;

  const breakStartMinutes = workingSettings?.enableMiddayBreak
    ? parseTimeToMinutes(workingSettings.breakStartTime || DEFAULT_BREAK_START_TIME)
    : null;
  const breakEndMinutes = workingSettings?.enableMiddayBreak
    ? parseTimeToMinutes(workingSettings.breakEndTime || DEFAULT_BREAK_END_TIME)
    : null;

  const hasActiveBreak =
    breakStartMinutes !== null &&
    breakEndMinutes !== null &&
    breakEndMinutes > breakStartMinutes;

  const endHourLabel = `${Math.floor(workdayEndMinutes / 60)
    .toString()
    .padStart(2, '0')}:${(workdayEndMinutes % 60).toString().padStart(2, '0')}`;

  // Calculer les jours de la semaine
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Lundi
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Générer les créneaux de la journée
  const generateTimeSlots = () => {
    const slots: Array<{ startMinutes: number; endMinutes: number; label: string }> = [];

    for (
      let startMinutes = workdayStartMinutes;
      startMinutes + bookingStepMinutes <= workdayEndMinutes;
      startMinutes += bookingStepMinutes
    ) {
      const hours = Math.floor(startMinutes / 60)
        .toString()
        .padStart(2, '0');
      const minutes = (startMinutes % 60).toString().padStart(2, '0');

      slots.push({
        startMinutes,
        endMinutes: startMinutes + bookingStepMinutes,
        label: `${hours}:${minutes}`,
      });
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Filtrer les rendez-vous pour un jour et un créneau spécifiques
  const getAppointmentsForDaySlot = (day: Date, slotStartMinutes: number, slotEndMinutes: number) => {
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.startTime);
      const appointmentEndDate = new Date(appointment.endTime);
      const appointmentStartMinutes = appointmentDate.getHours() * 60 + appointmentDate.getMinutes();
      const appointmentEndMinutes = appointmentEndDate.getHours() * 60 + appointmentEndDate.getMinutes();

      return (
        format(appointmentDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') &&
        hasMinutesOverlap(
          appointmentStartMinutes,
          appointmentEndMinutes,
          slotStartMinutes,
          slotEndMinutes
        )
      );
    });
  };

  const getStartingAppointmentsForDaySlot = (day: Date, slotStartMinutes: number, slotEndMinutes: number) => {
    return appointments.filter((appointment) => {
      const appointmentDate = new Date(appointment.startTime);
      const appointmentStartMinutes = appointmentDate.getHours() * 60 + appointmentDate.getMinutes();

      return (
        format(appointmentDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') &&
        appointmentStartMinutes >= slotStartMinutes &&
        appointmentStartMinutes < slotEndMinutes
      );
    });
  };

  const isSlotDuringBreak = (slotStartMinutes: number, slotEndMinutes: number) => {
    if (!hasActiveBreak || breakStartMinutes === null || breakEndMinutes === null) {
      return false;
    }

    return hasMinutesOverlap(slotStartMinutes, slotEndMinutes, breakStartMinutes, breakEndMinutes);
  };

  // Vérifier si c'est aujourd'hui
  const isToday = (date: Date) => {
    return format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  };

  const handleSlotClick = (day: Date, slotStartMinutes: number) => {
    const slotDateTime = new Date(day);
    slotDateTime.setHours(0, 0, 0, 0);
    slotDateTime.setMinutes(slotStartMinutes);
    onTimeSlotClick(slotDateTime, CALENDAR_SETTINGS.defaultAppointmentDurationMinutes);
  };

  const handleAppointmentButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    appointment: Appointment
  ) => {
    event.stopPropagation();
    onAppointmentClick(appointment);
  };

  const getAppointmentLabel = (appointment: Appointment) => {
    if (appointment.title?.trim()) {
      return appointment.title;
    }

    const firstName = appointment.client?.firstName || '';
    const lastName = appointment.client?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || 'Rendez-vous';
  };

  const renderAppointmentButton = (appointment: Appointment, dense = false) => {
    const label = getAppointmentLabel(appointment);
    const appointmentStartTime = format(new Date(appointment.startTime), 'HH:mm');
    const appointmentEndTime = format(new Date(appointment.endTime), 'HH:mm');
    const appointmentTimeRange = `${appointmentStartTime} - ${appointmentEndTime}`;
    const denseCardClass = dense ? 'px-1.5 py-0.5' : APPOINTMENT_CARD_PADDING_COMPACT_CLASS;
    const cardHeight = dense
      ? WEEK_APPOINTMENT_CARD_DENSE_HEIGHT_PX
      : WEEK_APPOINTMENT_CARD_REGULAR_HEIGHT_PX;

    return (
      <button
        key={appointment.id}
        type="button"
        className={`${APPOINTMENT_CARD_BASE_CLASS} ${denseCardClass} ${getAppointmentStatusCardClass(appointment.status)} flex flex-col justify-center`}
        style={{ height: `${cardHeight}px` }}
        onClick={(event) => handleAppointmentButtonClick(event, appointment)}
        aria-label={`Voir le rendez-vous ${label} de ${appointmentStartTime} a ${appointmentEndTime}`}
      >
        {dense ? (
          <div className="truncate text-[10px] font-medium leading-tight">
            {appointmentTimeRange} · {label}
          </div>
        ) : (
          <>
            <div className={APPOINTMENT_CARD_TITLE_CLASS}>
              {label}
            </div>
            <div className={APPOINTMENT_CARD_TIME_CLASS}>
              {appointmentTimeRange}
            </div>
          </>
        )}
      </button>
    );
  };

  const maxAppointmentsPerCell = 2;

  return (
    <div className="relative h-full overflow-y-auto overflow-x-auto">
      <div className="min-w-full">
        {/* En-tête avec les jours */}
        <div className="grid grid-cols-8 border-b border-gray-200 bg-gray-50 sticky top-0 z-30">
          <div className="h-12 px-3 border-r border-gray-200 flex items-center">
            <div className="text-sm font-medium text-gray-700">Heure</div>
          </div>
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={`h-12 px-3 border-r border-gray-200 last:border-r-0 flex flex-col items-center justify-center ${
                isToday(day) ? 'bg-blue-50' : ''
              }`}
            >
              <div className="text-sm font-medium text-gray-700">
                {format(day, 'EEE', { locale: fr })}
              </div>
              <span
                className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-semibold leading-none tabular-nums ${
                  isToday(day) ? 'bg-blue-600 text-white' : 'text-gray-900'
                }`}
              >
                {format(day, 'd')}
              </span>
            </div>
          ))}
        </div>

        {/* Grille horaire */}
        {timeSlots.map((slot) => {
          const dayStates = weekDays.map((day) => {
            const occupiedAppointments = getAppointmentsForDaySlot(day, slot.startMinutes, slot.endMinutes);
            const dayAppointments = getStartingAppointmentsForDaySlot(day, slot.startMinutes, slot.endMinutes);
            const sortedAppointments = [...dayAppointments].sort(
              (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
            );
            const visibleAppointments = sortedAppointments.slice(0, maxAppointmentsPerCell);
            const remainingCount = sortedAppointments.length - visibleAppointments.length;
            const hasDenseAppointments = denseMode && (visibleAppointments.length >= 2 || remainingCount > 0);
            const isSlotBlockedByBreak = isSlotDuringBreak(slot.startMinutes, slot.endMinutes);
            const isSlotBlockedByAppointment = occupiedAppointments.length > 0;
            const isSlotDisabled = isSlotBlockedByBreak || isSlotBlockedByAppointment;
            const renderedItemsCount = visibleAppointments.length + (remainingCount > 0 ? 1 : 0);
            const itemHeight = hasDenseAppointments
              ? WEEK_APPOINTMENT_CARD_DENSE_HEIGHT_PX
              : WEEK_APPOINTMENT_CARD_REGULAR_HEIGHT_PX;
            const itemGap = hasDenseAppointments
              ? WEEK_APPOINTMENT_GAP_DENSE_PX
              : WEEK_APPOINTMENT_GAP_REGULAR_PX;
            const minimumCellHeight =
              WEEK_CELL_VERTICAL_PADDING_PX +
              renderedItemsCount * itemHeight +
              Math.max(0, renderedItemsCount - 1) * itemGap;

            return {
              day,
              visibleAppointments,
              remainingCount,
              hasDenseAppointments,
              isSlotBlockedByBreak,
              isSlotDisabled,
              minimumCellHeight,
            };
          });

          const rowSlotHeight = Math.max(
            rowHeight,
            ...dayStates.map((state) => state.minimumCellHeight)
          );

          return (
          <div key={slot.startMinutes} className="grid grid-cols-8 border-b border-gray-100">
            {/* Colonne des heures */}
            <div
              className="px-3 border-r border-gray-200 bg-gray-50 flex items-center"
              style={{ height: `${rowSlotHeight}px` }}
            >
              <div className="text-sm text-gray-600">
                {slot.label}
              </div>
            </div>

            {/* Colonnes des jours */}
            {dayStates.map((state) => {
              const dayKey = format(state.day, 'yyyy-MM-dd');
              let slotStateClassName = isToday(state.day) ? 'hover:bg-slate-100' : 'hover:bg-blue-50';
              const contentContainerClassName = state.hasDenseAppointments
                ? 'relative z-10 h-full flex flex-col gap-0.5 pointer-events-none'
                : 'relative z-10 h-full flex flex-col gap-1 pointer-events-none';
              const overflowCardPaddingClass = state.hasDenseAppointments
                ? 'px-1.5 py-0.5 text-[10px]'
                : `${APPOINTMENT_CARD_PADDING_COMPACT_CLASS} text-[11px]`;

              if (state.isSlotDisabled) {
                if (state.isSlotBlockedByBreak) {
                  slotStateClassName = 'bg-amber-50 cursor-not-allowed';
                } else {
                  slotStateClassName = isToday(state.day)
                    ? 'bg-blue-50 cursor-not-allowed'
                    : 'bg-gray-50 cursor-not-allowed';
                }
              }

              return (
                <div
                  key={`${dayKey}-${slot.startMinutes}`}
                  className={`relative p-2 border-r border-gray-200 last:border-r-0 transition-colors ${
                    isToday(state.day) ? 'bg-blue-50' : ''
                  }`}
                  style={{ height: `${rowSlotHeight}px` }}
                >
                  <button
                    type="button"
                    className={`absolute inset-0 ${slotStateClassName}`}
                    onClick={() => handleSlotClick(state.day, slot.startMinutes)}
                    aria-label={`Créer un rendez-vous le ${format(state.day, 'EEEE d MMMM', { locale: fr })} à ${slot.label}`}
                    disabled={state.isSlotDisabled}
                  />
                  <div className={contentContainerClassName}>
                    {state.visibleAppointments.map((appointment) => (
                      <div key={appointment.id} className="pointer-events-auto">
                        {renderAppointmentButton(appointment, state.hasDenseAppointments)}
                      </div>
                    ))}
                    {state.remainingCount > 0 && (
                      <div
                        className={`${APPOINTMENT_CARD_BASE_CLASS} ${overflowCardPaddingClass} border-l-4 border-slate-400 bg-slate-100 text-slate-700 cursor-default flex flex-col justify-center`}
                        style={{
                          height: `${state.hasDenseAppointments ? WEEK_APPOINTMENT_CARD_DENSE_HEIGHT_PX : WEEK_APPOINTMENT_CARD_REGULAR_HEIGHT_PX}px`
                        }}
                      >
                        +{state.remainingCount} autre(s)
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )})}

        <div className="sticky bottom-0 z-20 grid grid-cols-8 border-t border-slate-300 bg-white/95 backdrop-blur pointer-events-none">
          <div className="border-r border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700">
            {endHourLabel}
          </div>
          <div className="col-span-7 px-3 py-1 text-[11px] text-slate-500">
            Fin de journee
          </div>
        </div>
      </div>

      {/* Message si aucun rendez-vous */}
      {appointments.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-500">
            <div className="text-lg font-medium">Aucun rendez-vous cette semaine</div>
            <div className="text-sm">Cliquez sur un créneau pour en créer un</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekView;